import { invalidateAfterUserGroupMemberMutation, noddiAsync, URLKeys } from 'noddi-async';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiBadge, NoddiButton } from 'noddi-ui-web';
import { WorkerAvatar } from '../ServiceWorker/WorkerAvatar';
import { CarChangeRow } from './CarChangeRow';
import { buildDriverChangeEffects, type DriverChange, type DriverChangeParty } from './driverChangePlan';
import { acceptsAnyCarSms } from './notificationReach';
import { NotificationGroupOverviewTranslations } from './notificationTranslations';
import { SectionHeading } from './SectionHeading';

type Props = {
  userGroupId: number;
  changes: DriverChange[];
  /**
   * `car` when the sheet header already names the car, so a row only has to name the
   * two people; `member` when it already names the driver, so a row names the car.
   * Repeating what the header says is what made this read as cards inside cards.
   */
  subject: 'car' | 'member';
  /** Lets the review fix the silenced-driver warning it just raised, instead of only reporting it. */
  canEditNotifications: boolean;
  translations: NotificationGroupOverviewTranslations;
};

/**
 * Step two, shared by both directions. The picker answers "who", this answers
 * "and what does that do" — the part the flow used to leave to a toast.
 */
export const DriverChangeReview = ({ userGroupId, changes, subject, canEditNotifications, translations: t }: Props) => {
  const effects = buildDriverChangeEffects({ changes, translations: t });
  const incoming = changes[0]?.incoming;

  return (
    <div className='flex flex-col gap-4'>
      <ul className='flex flex-col divide-y divide-border/60'>
        {changes.map((change) => (
          <li key={change.userGroupCarId} className='py-2'>
            {subject === 'car' ? (
              <div className='flex min-w-0 items-center gap-2'>
                <Person party={change.outgoing} fallback={t.noDriver} />
                <NoddiIcon name='ArrowRight' size='small' className='shrink-0 text-muted-foreground' />
                <Person party={change.incoming} fallback={t.noDriver} />
              </div>
            ) : (
              <CarChangeRow
                carName={change.carName}
                licensePlateNumber={change.licensePlateNumber}
                note={change.outgoing ? t.takingOverFrom(change.outgoing.name) : t.noDriver}
              />
            )}
          </li>
        ))}
      </ul>

      <div className='flex flex-col gap-2 rounded-xl border border-border/60 p-3'>
        <SectionHeading icon='ListCheck'>{t.whatHappens}</SectionHeading>
        <ul className='flex flex-col gap-2'>
          {effects.map((effect) => (
            <li key={effect.text} className='flex items-start gap-2 text-sm'>
              <NoddiIcon
                name={effect.tone === 'warning' ? 'TriangleAlert' : 'CheckCircle'}
                size='small'
                className={cn('mt-0.5 shrink-0', effect.tone === 'warning' ? 'text-yellow-600' : 'text-successDark')}
              />
              <span className={cn('min-w-0', effect.tone === 'warning' && 'text-yellow-900')}>{effect.text}</span>
            </li>
          ))}
        </ul>
      </div>

      {incoming && !acceptsAnyCarSms(incoming) && canEditNotifications && (
        <TurnOnSmsButton userGroupId={userGroupId} party={incoming} t={t} />
      )}
    </div>
  );
};

// Both sides read the same: the arrow between them already says which way the car goes.
const Person = ({ party, fallback }: { party?: DriverChangeParty; fallback: string }) => (
  <span className='flex min-w-0 flex-1 items-center gap-2'>
    {party ? (
      <>
        <WorkerAvatar name={party.name} size='sm' />
        <span className='min-w-0 truncate text-sm'>{party.name}</span>
      </>
    ) : (
      <span className='min-w-0 truncate text-sm text-muted-foreground'>{fallback}</span>
    )}
  </span>
);

/**
 * Colocated with the warning it clears: the admin can see the silenced driver and
 * fix it without leaving the review and losing the selection.
 */
const TurnOnSmsButton = ({
  userGroupId,
  party,
  t
}: {
  userGroupId: number;
  party: DriverChangeParty;
  t: NotificationGroupOverviewTranslations;
}) => {
  const { mutate, isPending } = noddiAsync.usePatch({
    type: URLKeys.patchUserGroupMember,
    queryConfig: { onSuccess: () => invalidateAfterUserGroupMemberMutation({ userGroupId }) }
  });

  return (
    <div className='flex flex-wrap items-center gap-2'>
      <NoddiBadge variant='warning'>{t.smsTurnedOff}</NoddiBadge>
      <NoddiButton
        variant='outline'
        size='small'
        startIcon='MessageSquare'
        loading={isPending}
        onClick={() =>
          mutate({
            userGroupMemberId: party.userGroupMemberId,
            smsBookingNotifications: true,
            smsRecallProposals: true
          })
        }
      >
        {t.turnOnSmsForMember(party.name)}
      </NoddiButton>
    </div>
  );
};
